import {
  Autocomplete,
  Box,
  createFilterOptions,
  FormControl,
  FormLabel,
  MenuItem,
  Stack,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { CANDIDATE_STATUS_DEFAULTS, CandidateStatusIcon } from "../../App/Components/CandidateStatus";
import { useCandidateStatusDefinitionsQuery } from "../../App/Queries/CandidateStatusQueries";
import { BaseFormField, ICampaignFormState } from "../../App/Typings/Campaigns";
import { IClientCustomFieldData } from "../../App/Typings/Client";
import { LocationValue, Rule } from "../../App/Typings/DynamicGroupRules";
import { IGroup } from "../../App/Typings/Groups";
import { FieldType } from "../DynamicGroup";
import { generateId } from "./DynamicFilterRules";
import { DynamicLocationField } from "./DynamicLocationField";

interface DynamicFilterRuleValueProps {
  rule: Rule;
  index: number;
  fieldOptions: FieldType[];
  campaigns?: ICampaignFormState[];
  tags?: string[];
  updateRuleValue: (id: string, field: string, value: any) => void;
  groups?: IGroup[];
  customFieldData?: IClientCustomFieldData;
}

const formLabelSxProps: SxProps<Theme> = {
  minHeight: "1.75rem",
  display: "flex",
  alignItems: "center",
};

export function DynamicFilterRuleValue({
  rule,
  index,
  fieldOptions,
  campaigns,
  tags,
  updateRuleValue,
  groups,
  customFieldData,
}: DynamicFilterRuleValueProps) {
  const type = fieldOptions.find((f) => f.id === rule.filter.field)?.field.fieldType;
  const { data: statusDefinitions } = useCandidateStatusDefinitionsQuery();

  const getUniqueDropDownOptions = (
    field: BaseFormField | undefined,
    fieldId: string,
    customFieldData?: IClientCustomFieldData,
  ): string[] => {
    const allOptions: string[] = [];

    // Add form field dropdown options
    field?.dropdownOptions.forEach((f) => {
      if (!allOptions.includes(f)) {
        allOptions.push(f);
      }
    });

    // Add custom field data values if available
    if (customFieldData && fieldId.startsWith("customfield.")) {
      const fieldKey = fieldId.replace("customfield.", "");
      const customValues = customFieldData[fieldKey] || [];

      customValues.forEach((value) => {
        if (value && !allOptions.includes(value)) {
          allOptions.push(value);
        }
      });
    }

    return allOptions.sort();
  };

  const getDropDownControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
    hasError: boolean,
  ) => {
    const filterOptions = createFilterOptions({
      matchFrom: "any",
      limit: 500,
    });

    const uniqueDropdownOptions = getUniqueDropDownOptions(
      fieldOptions.find((f) => f.id === rule.filter.field)?.field,
      rule.filter.field,
      customFieldData,
    );

    return (
      <>
        <FormLabel
          error={hasError}
          htmlFor={`dropdown-field-${index}`}
          id={`dropdown-field-${index}-label`}
          sx={formLabelSxProps}
        >
          Value
        </FormLabel>
        {uniqueDropdownOptions.length < 500 ? (
          <TextField
            data-test={`dropdown-select-${index}`}
            error={hasError}
            id={`dropdown-field-${index}`}
            select
            SelectProps={{
              multiple: true,
              value: rule.filter.value.value ? (rule.filter.value.value + "").split(",") : [],
              onChange: (e: any) => {
                onChange(rule.id, "value", e.target.value.join(","));
              },
              MenuProps: { "data-test": `dropdown-menu-${index}` } as any,
            }}
          >
            {uniqueDropdownOptions.map((option, i) => (
              <MenuItem
                value={fixedEncodeURIComponent(option)}
                key={`dropdown-options-value-${index}-${i}`}
                data-test={`dropdown-options-value-${index}-${i}`}
              >
                {option}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Autocomplete
            freeSolo
            data-test={`dropdown-select-${index}`}
            id={`dropdown-field-${index}`}
            disableClearable
            multiple
            options={uniqueDropdownOptions.map((o) => fixedEncodeURIComponent(o)) ?? []}
            filterOptions={filterOptions}
            onChange={(_e, value) => {
              onChange(rule.id, "value", value.join(","));
            }}
            getOptionLabel={(option: any) => decodeURIComponent(option)}
            value={rule.filter.value.value ? (rule.filter.value.value + "").split(",") : []}
            renderInput={(params) => (
              <TextField
                error={hasError}
                {...params}
                InputProps={{
                  ...params.InputProps,
                  type: "search",
                }}
                SelectProps={{
                  MenuProps: { "data-test": `dropdown-menu-${index}` } as any,
                }}
              />
            )}
          />
        )}
      </>
    );
  };

  const getTextControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
    hasError: boolean,
  ) => {
    return (
      <>
        <FormLabel
          error={hasError}
          htmlFor={`text-input-${index}`}
          id={`text-input-${index}-label`}
          sx={formLabelSxProps}
        >
          Value
        </FormLabel>
        <TextField
          data-test={`text-input-${index}`}
          error={hasError}
          id={`text-input-${index}`}
          value={rule.filter.value.value}
          onChange={(e) => onChange(rule.id, "value", e.target.value)}
        />
      </>
    );
  };

  const getDateControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
    hasError: boolean,
  ) => {
    return (
      <>
        <FormLabel
          error={hasError}
          htmlFor={`date-input-${index}`}
          id={`date-input-${index}-label`}
          sx={formLabelSxProps}
        >
          Value
        </FormLabel>
        <DatePicker
          value={rule.filter.value.value ?? ""}
          InputProps={{ id: `start-date-${index}` }}
          renderInput={(props) => (
            <TextField
              data-test={`date-input-${index}`}
              style={{ width: "90%" }}
              {...props}
              error={hasError}
              placeholder="Date"
              id={`date-input-${index}`}
            />
          )}
          onChange={(v: any) => {
            onChange(rule.id, "value", dayjs(v).isValid() ? dayjs(v).toISOString() : v?.toString());
          }}
        />
      </>
    );
  };

  const getDateRangeControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
    hasError: boolean,
  ) => {
    const startDate = dayjs(rule.filter.value.value.item1);
    const endDate = dayjs(rule.filter.value.value.item2);
    return (
      <>
        <FormLabel error={hasError} htmlFor={`date-input-${index}`} id={`date-input-${index}-label`}>
          Value
        </FormLabel>
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <DatePicker
            value={startDate}
            InputProps={{ id: `start-date-${index}` }}
            renderInput={(props) => (
              <TextField
                data-test={`date-range-start-input-${index}`}
                style={{ width: "90%" }}
                {...props}
                error={hasError}
                placeholder="Start Date"
                id={`date-range-input-startDate-${index}`}
              />
            )}
            maxDate={endDate.toDate()}
            onChange={(v) => {
              const date = dayjs(v).isValid() ? dayjs(v).toISOString() : startDate.toISOString();
              onChange(rule.id, "value", { value: { item1: date, item2: endDate.toISOString() } });
            }}
          />
          {"to"}
          <DatePicker
            value={endDate}
            InputProps={{ id: `end-date-${index}` }}
            renderInput={(props) => (
              <TextField
                data-test={`date-range-end-input-${index}`}
                style={{ width: "90%" }}
                {...props}
                error={hasError}
                placeholder="End Date"
                id={`date-range-input-endDate-${index}`}
              />
            )}
            minDate={startDate.toDate()}
            onChange={(v) => {
              const date = dayjs(v).isValid() ? dayjs(v).toISOString() : startDate.toISOString();
              onChange(rule.id, "value", { value: { item1: startDate.toISOString(), item2: date } });
            }}
          />
        </Stack>
      </>
    );
  };

  const getBooleanControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
    hasError: boolean,
  ) => {
    return (
      <>
        <FormLabel
          error={hasError}
          htmlFor={`boolean-input-${index}`}
          id={`boolean-input-${index}-label`}
          sx={formLabelSxProps}
        >
          Value
        </FormLabel>
        <TextField
          data-test={`boolean-select-${index}`}
          id={`boolean-field-${index}`}
          select
          SelectProps={{ MenuProps: { "data-test": `boolean-menu-${index}` } as any }}
          error={hasError}
          value={rule.filter.value.value}
          onChange={(e) => onChange(rule.id, "value", e.target.value)}
        >
          <MenuItem value="true" key={`boolean-value-${index}-${generateId(5)}`}>
            True
          </MenuItem>
          <MenuItem value="false" key={`boolean-value-${index}-${generateId(5)}`}>
            False
          </MenuItem>
        </TextField>
      </>
    );
  };

  const getLocationControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
    hasError: boolean,
  ) => {
    return (
      <>
        <FormLabel
          error={hasError}
          htmlFor={`location-input-${index}`}
          id={`location-input-${index}-label`}
          sx={formLabelSxProps}
        >
          Value
        </FormLabel>
        <DynamicLocationField
          locationValue={
            rule.filter.value
              ? (rule.filter.value as LocationValue)
              : ({ name: "", latitude: 0, longitude: 0, radius: 0 } as LocationValue)
          }
          id={`geolocation-${index}`}
          onChange={(value) => {
            onChange(rule.id, "value", value);
          }}
          key={`location-value-${index}`}
        />
      </>
    );
  };

  const getCampaignControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
  ) => {
    return (
      <>
        <FormLabel
          htmlFor={`campaign-input-${index}`}
          id={`campaign-input-${index}-label`}
          sx={formLabelSxProps}
        >
          Value
        </FormLabel>
        <TextField
          select
          id={`campaign-input-${index}`}
          SelectProps={{
            multiple: true,
            value: rule.filter.value.value
              ? (rule.filter.value.value + "").split(",").map((c: string) => c.trim())
              : [],
            onChange: (e: any) => {
              onChange(rule.id, "value", e.target.value.join(","));
            },
            MenuProps: { "data-test": `campaign-menu-${index}` } as any,
          }}
        >
          {campaigns?.map((c, i) => (
            <MenuItem value={c.campaignId} key={`campaign-value-${index}-${i}`}>
              {c.campaignName}
            </MenuItem>
          ))}
        </TextField>
      </>
    );
  };

  const getGroupControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
  ) => {
    return (
      <>
        <FormLabel htmlFor={`group-input-${index}`} id={`group-input-${index}-label`} sx={formLabelSxProps}>
          Value
        </FormLabel>
        <TextField
          select
          id={`group-input-${index}`}
          SelectProps={{
            multiple: true,
            value: rule.filter.value.value
              ? (rule.filter.value.value + "").split(",").map((c: string) => c.trim())
              : [],
            onChange: (e: any) => {
              onChange(rule.id, "value", e.target.value.join(","));
            },
            MenuProps: { "data-test": `group-menu-${index}` } as any,
          }}
        >
          {groups
            ?.filter((c) => !c.isDynamic)
            ?.map((c, i) => (
              <MenuItem value={c.id} key={`group-value-${index}-${i}`}>
                {c.name}
              </MenuItem>
            ))}
        </TextField>
      </>
    );
  };

  const getStatusControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
  ) => {
    const source =
      statusDefinitions && statusDefinitions.length ? statusDefinitions : CANDIDATE_STATUS_DEFAULTS;
    const options = [...source].sort((a, b) => a.sortOrder - b.sortOrder);

    return (
      <>
        <FormLabel htmlFor={`status-input-${index}`} id={`status-input-${index}-label`} sx={formLabelSxProps}>
          Value
        </FormLabel>
        <TextField
          style={{ width: "90%" }}
          select
          id={`status-input-${index}`}
          data-test={`status-input-${index}`}
          SelectProps={{
            multiple: true,
            value: rule.filter.value.value
              ? (rule.filter.value.value + "").split(",").map((c: string) => c.trim())
              : [],
            onChange: (e: any) => {
              onChange(rule.id, "value", e.target.value.join(","));
            },
            MenuProps: { "data-test": `status-menu-${index}` } as any,
          }}
        >
          {options.map((o, i) => (
            <MenuItem
              value={o.color}
              key={`status-value-${index}-${i}`}
              data-test={`status-option-${o.color}-${index}`}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CandidateStatusIcon statusColor={o.color} />
                <span>{o.label}</span>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </>
    );
  };

  const getTagsControl = (
    rule: Rule,
    index: number,
    onChange: (id: string, field: string, value: any) => void,
  ) => {
    return (
      <>
        <FormLabel htmlFor={`tag-input-${index}`} id={`tag-input-${index}-label`}>
          Value
        </FormLabel>
        <TextField
          style={{ width: "90%" }}
          select
          id={`tag-input-${index}`}
          data-test={`tag-input-${index}`}
          SelectProps={{
            multiple: true,
            value: rule.filter.value.value
              ? (rule.filter.value.value + "").split(",").map((c: string) => c.trim())
              : [],
            onChange: (e: any) => {
              onChange(rule.id, "value", e.target.value.join(","));
            },
            MenuProps: { "data-test": `tag-menu-${index}` } as any,
          }}
        >
          {tags?.map((c, i) => (
            <MenuItem value={fixedEncodeURIComponent(c)} key={`tag-value-${index}-${i}`}>
              {c}
            </MenuItem>
          ))}
        </TextField>
      </>
    );
  };

  let control = getTextControl(rule, index, updateRuleValue, !rule.filter.value || !rule.filter.value.value);

  if (rule.filter.field === "groupIds") {
    control = getGroupControl(rule, index, updateRuleValue);
  } else if (
    (rule.filter.field === "campaignResponses.campaignId" ||
      rule.filter.field === "CandidateResponse.campaignId") &&
    campaigns &&
    campaigns.length > 0
  ) {
    control = getCampaignControl(rule, index, updateRuleValue);
  } else if (rule.filter.field === "tags") {
    control = getTagsControl(rule, index, updateRuleValue);
  } else if (rule.filter.field === "statusColor") {
    control = getStatusControl(rule, index, updateRuleValue);
  } else if (type === "dropdown" || type === "interestPicker" || type === "multiSelect") {
    control = getDropDownControl(
      rule,
      index,
      updateRuleValue,
      !rule.filter.value || !rule.filter.value.value,
    );
  } else if (rule.filter.type === "datetime" && rule.filter.command !== "between") {
    control = getDateControl(rule, index, updateRuleValue, validateDateValue(rule));
  } else if (rule.filter.type === "datetime" && rule.filter.command === "between") {
    control = getDateRangeControl(rule, index, updateRuleValue, validateDateRangeValue(rule));
  } else if (rule.filter.type === "boolean" || rule.filter.type === "upload") {
    control = getBooleanControl(
      rule,
      index,
      updateRuleValue,
      !rule.filter.value || rule.filter.value.value === undefined || rule.filter.value.value === "",
    );
  } else if (rule.filter.type === "location") {
    control = getLocationControl(rule, index, updateRuleValue, validateLocationValue(rule));
  }

  return <FormControl sx={{ width: "90%" }}>{control}</FormControl>;
}

const validateDateValue = (rule: Rule) => {
  return !rule.filter.value || !rule.filter.value.value || !dayjs(rule.filter.value.value).isValid();
};

const validateDateRangeValue = (rule: Rule) => {
  if (!rule.filter.value || !rule.filter.value.value.item1 || !rule.filter.value.value.item2) {
    return true;
  }
  return !dayjs(rule.filter.value.value.item1).isValid() || !dayjs(rule.filter.value.value.item2).isValid();
};

const validateLocationValue = (rule: Rule) => {
  const location = rule.filter.value as LocationValue;

  if (!location.name) return true;
  if (location.latitude === 0 || location.longitude === 0) return true;
  if (!location.radius || location.radius <= 0) return true;

  return false;
};

function fixedEncodeURIComponent(str: string) {
  return encodeURIComponent(str).replace(/[!'()*]/g, function (c) {
    return "%" + c.charCodeAt(0).toString(16);
  });
}
